import { useCallback, useEffect, useState } from 'react'
import { IAnimal } from 'src/dto/animal.dto'
import { useGetAnimalList } from 'src/hooks/api/animal'

interface Genealogy {
  self: IAnimal
  parents?: {
    father?: IAnimal
    mother?: IAnimal
  }
  paternalGrandparents?: {
    paternalGrandfather?: IAnimal
    paternalGrandmother?: IAnimal
  }
  maternalGrandparents?: {
    maternalGrandfather?: IAnimal
    maternalGrandmother?: IAnimal
  }
}

export const useGenealogicalDiagram = (dataAnimal: IAnimal) => {
  const [genealogyData, setGenealogyData] = useState<Genealogy>()
  const [hierarchyDiagramState, setHierarchyDiagramState] = useState({
    paternalGrandparentsIsExpanded: false,
    maternalGrandparentsExpanded: false
  })

  const isMobile = window.innerWidth <= 768
  const [zoom, setZoom] = useState(0.5)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const [startPosition, setStartPosition] = useState(position)
  const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null)
  const [initialZoom, setInitialZoom] = useState(0.5)

  const { data: animalListData } = useGetAnimalList()
  const animalList = animalListData?.pages.flatMap((page) => page.list) ?? []

  const handleWheel = useCallback((event: React.WheelEvent) => {
    setZoom((prevZoom) => Math.min(Math.max(prevZoom - event.deltaY * 0.001, 0.5), 2))
  }, [])

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      setDragging(true)
      setStartPosition({ x: event.clientX - position.x, y: event.clientY - position.y })
    },
    [position]
  )

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!dragging) return
      setPosition({
        x: event.clientX - startPosition.x,
        y: event.clientY - startPosition.y
      })
    },
    [dragging, startPosition]
  )

  const handleMouseUp = useCallback(() => {
    setDragging(false)
  }, [])

  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      if (event.touches.length === 2) {
        // Inicia o gesto de pinça
        const distance = Math.hypot(
          event.touches[0].clientX - event.touches[1].clientX,
          event.touches[0].clientY - event.touches[1].clientY
        )
        setInitialPinchDistance(distance)
        setInitialZoom(zoom)
      } else if (event.touches.length === 1) {
        // Inicia o arrasto
        const touch = event.touches[0]
        setDragging(true)
        setStartPosition({ x: touch.clientX - position.x, y: touch.clientY - position.y })
      }
    },
    [position, zoom]
  )

  const handleTouchMove = useCallback(
    (event: React.TouchEvent) => {
      if (event.touches.length === 2 && initialPinchDistance !== null) {
        const distance = Math.hypot(
          event.touches[0].clientX - event.touches[1].clientX,
          event.touches[0].clientY - event.touches[1].clientY
        )
        const scaleFactor = distance / initialPinchDistance
        setZoom(Math.min(Math.max(initialZoom * scaleFactor, 0.5), 2))
      } else if (event.touches.length === 1 && dragging) {
        const touch = event.touches[0]
        setPosition({
          x: touch.clientX - startPosition.x,
          y: touch.clientY - startPosition.y
        })
      }
    },
    [dragging, initialPinchDistance, initialZoom, startPosition]
  )

  const handleTouchEnd = useCallback(() => {
    setDragging(false)
    setInitialPinchDistance(null)
  }, [])

  const handleZoomParents = useCallback(() => {
    const expandedParentsPostion = isMobile ? { x: 0, y: 300 } : { x: 60, y: 671 }
    setPosition(expandedParentsPostion)
    setZoom(isMobile ? 0.8 : 0.62)
  }, [isMobile])

  useEffect(() => {
    setTimeout(() => {
      setZoom(isMobile ? 0.6 : 1)
    }, 100)
  }, [])

  const getAnimalById = useCallback(
    (id: string | null): IAnimal | undefined => {
      if (!animalList) return undefined
      return animalList.find((animal) => animal.idAnimal === id)
    },
    [animalList]
  )

  const buildGenealogy = useCallback(
    (animal: IAnimal): Genealogy => {
      const father = getAnimalById(animal.idAnimalPai || null)
      const mother = getAnimalById(animal.idAnimalMae || null)

      const paternalGrandfather = father ? getAnimalById(father.idAnimalPai || null) : undefined
      const paternalGrandmother = father ? getAnimalById(father.idAnimalMae || null) : undefined

      const maternalGrandfather = mother ? getAnimalById(mother.idAnimalPai || null) : undefined
      const maternalGrandmother = mother ? getAnimalById(mother.idAnimalMae || null) : undefined

      return {
        self: {
          ...animal
        },
        parents: {
          father,
          mother
        },
        paternalGrandparents: {
          paternalGrandfather,
          paternalGrandmother
        },
        maternalGrandparents: {
          maternalGrandfather,
          maternalGrandmother
        }
      }
    },
    [getAnimalById]
  )

  useEffect(() => {
    if (animalList?.length && dataAnimal) {
      setGenealogyData((prevGenealogy) => {
        if (!prevGenealogy || prevGenealogy.self.idAnimal !== dataAnimal.idAnimal) {
          return buildGenealogy(dataAnimal)
        }
        return prevGenealogy
      })
    }
  }, [animalList, dataAnimal])

  return {
    genealogyData,
    hierarchyDiagramState,
    zoom,
    position,
    isMobile,
    setPosition,
    setZoom,
    handleZoomParents,
    setHierarchyDiagramState,
    handleWheel,
    handleMouseMove,
    handleMouseUp,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  }
}
